import { NgZone } from '@angular/core';
import { AuthService } from '@app/modules/auth';
import { EMPTY, interval, Observable, of, Subscription, timer } from 'rxjs';
import { catchError, delayWhen, map, retryWhen, switchMap, tap } from 'rxjs/operators';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import { Request, MDLogin, MDLoginResponse, MDLoginType, LoginResponseStatus } from '../../../../utProto/marketData_pb';
import { DEFAULT_SOCKET_CONFIG, Utils } from '../../../_helpers';
import { ConfigurationService } from '../configuration/configuration.service';
import { MIXPANEL_EVENTS } from '../mixpanel/mixpanel.events';
import { MixpanelService } from '../mixpanel/mixpanel.service';
import { HeartBeat, SOCKET_MSG_TYPES, SocketType, WS_APP_CLOSE_CODES, WS_CLOSE_CODES } from './websocket.enums';
import { DeSerializedMessage } from './websocket.interfaces';

type ForceLogoutReason = 'tokens_expired' | 'refresh_failed' | 'auth_failure';

/**
 * Base class for authenticated WebSocket services.
 *
 * Lifecycle:
 *   startConnection() → connect() opens the socket → server responds with
 *   Login_Response (binary or JSON) → onAuthenticated() drains the pending
 *   queue and starts a heartbeat → closeSocket() tears everything down on
 *   logout, fatal auth failure, or unrecoverable transport errors.
 *
 * Reconnect:
 *   Stream errors flow through retryWhen with exponential backoff capped at
 *   MAX_RETRY_MS. Token-related terminal errors stop retrying and force logout.
 *
 * Idle recovery:
 *   When the tab returns from being hidden ≥ LONG_HIDDEN_RESET_MS, the socket
 *   is closed, retry counter reset, and a fresh connection is started so that
 *   tab-throttled / frozen sockets do not appear connected but stale.
 *
 * Subclasses (MarketSocketService, BooksSocketService, ChatSocketService)
 * override messageDeSerializer / tapMessage / sendAuthMessage / sendHeartbeat
 * for protocol-specific framing.
 */
export class BaseSocket {
    binarySocket = false;
    pendingMessagesQueue = [];
    authenticatedSocket = false;
    connection$: WebSocketSubject<any>;
    RETRY_DELAY_MS = 3 * 1000;
    heartbeat_time = 10 * 1000;
    socketUrl: string;

    private MAX_RETRY_MS = 60 * 1000;
    private connectTime: number | null = null;
    private retryCount = 0;
    private tabHiddenAt: number | null = null;

    private readonly MIXPANEL_DEDUPE_WINDOW_MS: number;
    private readonly LONG_HIDDEN_RESET_MS = 60 * 1000;

    private socketSubscription: Subscription | null = null;
    private heartbeatSubscription: Subscription | null = null;
    private visibilityChangeHandler: () => void;
    private visibilityListenerAttached = false;
    private onlineHandler: () => void;
    private offlineHandler: () => void;
    private networkListenersAttached = false;
    private lastOfflineAt: number | null = null;
    private lastOnlineAt: number | null = null;
    private shouldRetry = true;
    private hasAuthenticatedOnce = false;
    private mixpanelDedupeState = new Map<string, { lastSentAt: number; suppressedCount: number }>();

    constructor(
        protected authProvider: AuthService,
        private mixpanel: MixpanelService,
        private configService: ConfigurationService,
        private socketType: string,
        private ngZone: NgZone
    ) {
        const socketConfig = this.configService.remoteConfigCacheObj?.SOCKET_CONFIG || DEFAULT_SOCKET_CONFIG;
        this.heartbeat_time = this.getTimeMs(
            socketConfig.heartbeat_interval_seconds,
            DEFAULT_SOCKET_CONFIG.heartbeat_interval_seconds
        );
        this.MAX_RETRY_MS = this.getTimeMs(
            socketConfig.max_retry_delay_seconds,
            DEFAULT_SOCKET_CONFIG.max_retry_delay_seconds
        );
        this.MIXPANEL_DEDUPE_WINDOW_MS = this.getTimeMs(
            socketConfig.mixpanel_dedupe_window_seconds,
            DEFAULT_SOCKET_CONFIG.mixpanel_dedupe_window_seconds
        );

        this.visibilityChangeHandler = () => {
            if (document.visibilityState === 'hidden') {
                this.tabHiddenAt = Date.now();
                return;
            }
            const hiddenDuration = this.tabHiddenAt ? Date.now() - this.tabHiddenAt : 0;
            this.tabHiddenAt = null;
            if (!this.authProvider.isAuthenticated) return;
            if (hiddenDuration < this.LONG_HIDDEN_RESET_MS) return;
            this.ngZone.run(() => {
                this.closeSocket();
                this.retryCount = 0;
                this.startConnection();
            });
        };

        // The browser fires `offline` as soon as the OS detects loss of network
        // reachability. We can't rely on the WebSocket itself to surface a close
        // event in any reasonable time (TCP keepalives are extremely long, and
        // pending writes may queue silently), so we track the disconnect
        // ourselves and tear the socket down cleanly. Note: do NOT call
        // `connection$.error(...)` — rxjs 6's WebSocketSubject requires that
        // argument to be `{ code, reason }` and substitutes a misleading
        // TypeError message into the stream otherwise.
        this.offlineHandler = () => {
            // Idempotency: ignore repeat `offline` events while already offline.
            if (this.lastOfflineAt && (!this.lastOnlineAt || this.lastOnlineAt < this.lastOfflineAt)) return;
            this.lastOfflineAt = Date.now();
            this.ngZone.run(() => {
                this.trackMixpanel(
                    MIXPANEL_EVENTS.SOCKET_ERROR,
                    {
                        socket_type: this.socketType,
                        socket_url: Utils.sanitizeUrl(this.socketUrl),
                        close_reason: 'network_offline',
                        clean_close: false,
                        session_duration_ms: this.connectTime ? Date.now() - this.connectTime : 0,
                        retry_count: this.retryCount,
                        internet_connected: false,
                    },
                    `socket_error_${this.socketType}`
                );
                // Clean teardown via complete() — `online` will start a fresh socket.
                this.closeSocket();
            });
        };

        // On `online`, reconnect immediately. closeSocket() above already cleared
        // state; calling it again is a safe no-op if the user toggled wifi twice
        // in a row or events arrived out of order.
        this.onlineHandler = () => {
            // Idempotency: ignore repeat `online` events while already online.
            if (this.lastOnlineAt && (!this.lastOfflineAt || this.lastOnlineAt > this.lastOfflineAt)) return;
            this.lastOnlineAt = Date.now();
            if (!this.authProvider.isAuthenticated) return;
            this.ngZone.run(() => {
                this.closeSocket();
                this.retryCount = 0;
                this.startConnection();
            });
        };
    }

    startConnection(): void {
        if (!this.authProvider.isAuthenticated) {
            if (this.connection$ || this.socketSubscription) {
                this.closeSocket({ disableRetry: true });
            }
            return;
        }

        if (!this.visibilityListenerAttached) {
            document.addEventListener('visibilitychange', this.visibilityChangeHandler);
            this.visibilityListenerAttached = true;
        }

        if (!this.networkListenersAttached) {
            window.addEventListener('online', this.onlineHandler);
            window.addEventListener('offline', this.offlineHandler);
            this.networkListenersAttached = true;
        }

        this.shouldRetry = true;

        if (this.socketSubscription && !this.socketSubscription.closed) {
            if (this.connection$ && !this.connection$.closed) return;
            this.socketSubscription.unsubscribe();
        }

        this.socketSubscription = this.connect().subscribe({
            error: (error) => {
                this.socketSubscription = null;
                console.error('Socket stream error', error);
            },
            complete: () => {
                this.socketSubscription = null;
            }
        });
    }

    sendHeartbeat(): void {
        this.connection$.next(JSON.stringify({ command: HeartBeat.Heart_Beat }));
    }

    connect(): Observable<DeSerializedMessage> {
        if (!this.socketUrl) {
            console.error(`${this.socketType}: socket URL is not set`);
            return EMPTY;
        }

        return of(this.socketUrl).pipe(
            switchMap((wsUrl: string) => {
                if (!this.connection$ || this.connection$.closed) {
                    this.authenticatedSocket = false;
                    const socket = webSocket({
                        url: wsUrl,
                        binaryType: 'arraybuffer',
                        serializer: (value: any) => value as ArrayBuffer,
                        deserializer: (event: MessageEvent) => this.binarySocket ? event.data : JSON.parse(event.data as string),
                        openObserver: {
                            next: () => {
                                if (this.connection$ !== socket) return;
                                this.authenticateSocket();
                                if (this.heartbeatSubscription) this.heartbeatSubscription.unsubscribe();
                                this.heartbeatSubscription = interval(this.heartbeat_time).subscribe(() => this.sendHeartbeat());
                            }
                        },
                        closeObserver: {
                            next: (closeEvent: CloseEvent) => {
                                if (this.connection$ !== socket) return;
                                this.authenticatedSocket = false;
                                this.connectTime = null;
                            }
                        }
                    });
                    this.connection$ = socket;
                }

                return this.connection$.pipe(
                    map((msg: unknown) => this.messageDeSerializer(msg)),
                    tap((msg) => this.ngZone.run(() => this.tapMessage(msg)))
                );
            }),
            retryWhen((errors) => errors.pipe(
                switchMap((err: any) => {
                    if (!this.shouldRetry) return EMPTY;

                    if (!this.authProvider.isAuthenticated) {
                        this.shouldRetry = false;
                        this.authenticatedSocket = false;
                        return EMPTY;
                    }

                    const streamReason = this.getSocketStreamErrorReason(err);
                    if (streamReason === 'tokens_expired' || streamReason === 'refresh_failed' || streamReason === 'auth_failure') {
                        this.shouldRetry = false;
                        this.authenticatedSocket = false;
                        return EMPTY;
                    }

                    const duration = this.connectTime ? Date.now() - this.connectTime : 0;
                    const isCloseEvent = typeof CloseEvent !== 'undefined' && err instanceof CloseEvent;
                    const closeCode = isCloseEvent ? err.code : (typeof err?.code === 'number' ? err.code : undefined);
                    const wasClean = isCloseEvent ? err.wasClean : false;
                    const closeReason = isCloseEvent
                        ? this.getCloseCodeMeaning(closeCode)
                        : (streamReason || 'unknown_error');
                    this.trackMixpanel(
                        MIXPANEL_EVENTS.SOCKET_ERROR,
                        {
                            socket_type: this.socketType,
                            socket_url: Utils.sanitizeUrl(this.socketUrl),
                            close_reason: closeReason,
                            clean_close: wasClean,
                            session_duration_ms: duration,
                            retry_count: this.retryCount,
                            internet_connected: navigator.onLine,
                        },
                        `socket_error_${this.socketType}`
                    );

                    this.retryCount += 1;
                    this.authenticatedSocket = false;

                    // If the OS reports we're offline, stop burning retries; the
                    // `online` handler will restart the connection immediately.
                    if (!navigator.onLine) return EMPTY;

                    if (this.authProvider.accessTokenObj.isExpired) {
                        return this.authProvider.refreshToken().pipe(
                            catchError((error) => {
                                console.error(error);
                                this.trackMixpanel(
                                    MIXPANEL_EVENTS.SOCKET_ERROR,
                                    {
                                        socket_type: this.socketType,
                                        socket_url: Utils.sanitizeUrl(this.socketUrl),
                                        close_reason: 'refresh_failed_retry',
                                        retry_count: this.retryCount,
                                        internet_connected: navigator.onLine,
                                    },
                                    `refresh_failed_retry_${this.socketType}`
                                );
                                this.forceLogout('refresh_failed');
                                return EMPTY;
                            })
                        );
                    }

                    return of(null);
                }),
                delayWhen(() => timer(Math.min(
                    this.RETRY_DELAY_MS * Math.pow(2, this.retryCount - 1),
                    this.MAX_RETRY_MS
                )))
            ))
        );
    }

    getCloseCodeMeaning(code: number): string {
        if (WS_CLOSE_CODES[code]) return WS_CLOSE_CODES[code];
        // RFC 6455 reserves 4000-4999 for application-defined close codes.
        if (code >= 4000) return `app_code_${code}`;
        return `unknown_${code}`;
    }

    /** RxJS WebSocketSubject errors use `{ code, reason }`, not `Error`. */
    protected getSocketStreamErrorReason(err: unknown): string | undefined {
        if (typeof CloseEvent !== 'undefined' && err instanceof CloseEvent) {
            return this.getCloseCodeMeaning(err.code);
        }
        if (err != null && typeof err === 'object') {
            const payload = err as { reason?: string; message?: string };
            return payload.reason ?? payload.message;
        }
        return undefined;
    }

    protected closeSocketStream(code: number, reason: string): void {
        if (!this.connection$ || this.connection$.closed) return;
        this.connection$.error({ code, reason });
    }

    messageDeSerializer(msg: unknown): DeSerializedMessage {
        if (this.binarySocket) {
            const buffer = msg as ArrayBuffer;
            const uintArr = new Uint8Array(buffer);
            return {
                messageType: Utils.ntohl(buffer, 4),
                message: uintArr.slice(4)
            };
        }
        const obj = msg as { response?: number; CommandCategory?: number };
        // messageType: 0 -> unknown JSON message
        return {
            messageType: (obj?.response || 0) || (obj?.CommandCategory || 0),
            message: msg
        };
    }

    tapMessage(msg: DeSerializedMessage): void {
        try {
            if (msg.messageType !== SOCKET_MSG_TYPES.Login_Response) return;

            if (this.binarySocket) {
                const authResponse = MDLoginResponse.deserializeBinary(msg.message);
                if (authResponse.getLoginStatus() === LoginResponseStatus.LOGINRESPONSESTATUS_SUCCESS) {
                    this.onAuthenticated();
                } else {
                    this.handleAuthFailure();
                }
                return;
            }

            if (msg.message.login_status === LoginResponseStatus.LOGINRESPONSESTATUS_SUCCESS) {
                this.onAuthenticated();
            } else {
                this.handleAuthFailure();
            }
        } catch (error) {
            this.trackSocketError(msg?.messageType ?? null, error);
            console.error('Error in tapping message', error);
        }
    }

    protected trackSocketError(messageType: number | null, error: any): void {
        try {
            this.trackMixpanel(
                MIXPANEL_EVENTS.SOCKET_MESSAGE_ERROR,
                {
                    socket_url: Utils.sanitizeUrl(this.socketUrl),
                    socket_type: this.socketType,
                    message_type: messageType,
                    error: error?.message ?? String(error),
                    internet_connected: navigator.onLine,
                },
                `socket_message_error_${this.socketType}_${messageType ?? 'unknown'}`
            );
        } catch (trackingError) {
            console.error('Error in tracking socket error', trackingError);
        }
    }

    handleAuthFailure(): void {
        if (this.socketType === SocketType.UTRADE_AI_SOCKET) return;
        this.trackMixpanel(
            MIXPANEL_EVENTS.SOCKET_AUTH_FAILED,
            {
                socket_type: this.socketType,
                socket_url: Utils.sanitizeUrl(this.socketUrl),
                retry_count: this.retryCount,
                internet_connected: navigator.onLine,
            },
            `auth_failure_${this.socketType}`
        );
        this.shouldRetry = false;
        this.closeSocketStream(WS_APP_CLOSE_CODES.AUTH_FAILURE, 'auth_failure');
    }

    authenticateSocket(): void {
        if (!this.shouldRetry) return;

        if (this.authProvider.isAuthenticated && !this.authProvider.accessTokenObj.isExpired) {
            this.sendAuthMessage();
            return;
        }

        if (this.authProvider.refreshTokenObj && !this.authProvider.refreshTokenObj.isExpired) {
            this.authProvider.refreshToken().subscribe(
                () => this.sendAuthMessage(),
                () => this.forceLogout('refresh_failed')
            );
            return;
        }

        this.forceLogout('tokens_expired');
    }

    sendAuthMessage(): void {
        if (this.binarySocket) {
            const commandCategory = Utils.htonl(Request.REQUEST_LOGIN);
            const req = new MDLogin();
            req.setLoginType(MDLoginType.MDLOGINTYPE_JWT_TOKEN);
            req.setToken(this.authProvider.userInfo.access);
            const loginProtoBy = req.serializeBinary();
            const commandCategoryBuff = new Uint8Array(commandCategory);
            const loginReq = new Uint8Array(commandCategoryBuff.byteLength + loginProtoBy.length);
            loginReq.set(commandCategoryBuff, 0);
            loginReq.set(loginProtoBy, commandCategoryBuff.byteLength);
            this.connection$.next(loginReq);
            return;
        }

        this.connection$.next(JSON.stringify({
            command: Request.REQUEST_LOGIN,
            login_type: MDLoginType.MDLOGINTYPE_JWT_TOKEN,
            token: this.authProvider.userInfo.access
        }));
    }

    onAuthenticated(): void {
        const wasUnauthenticated = !this.authenticatedSocket;
        this.authenticatedSocket = true;
        if (this.hasAuthenticatedOnce && wasUnauthenticated) this.onRetry();
        this.hasAuthenticatedOnce = true;

        this.connectTime = Date.now();
        this.trackMixpanel(
            MIXPANEL_EVENTS.SOCKET_CONNECTED,
            {
                socket_type: this.socketType,
                socket_url: Utils.sanitizeUrl(this.socketUrl),
                retry_count: this.retryCount,
                is_reconnection: this.retryCount > 0,
                internet_connected: navigator.onLine,
            },
            `socket_connected_${this.socketType}`,
            true
        );
        this.retryCount = 0;

        while (this.pendingMessagesQueue.length) {
            this.send(this.pendingMessagesQueue.shift());
        }
        this.afterOnAuthenticated();
    }

    protected afterOnAuthenticated(): void { }

    protected onRetry(): void { }

    send(data: any): void {
        if (this.connection$ && !this.connection$.closed && this.authenticatedSocket) {
            if (this.binarySocket) {
                this.connection$.next(data);
            } else {
                this.connection$.next(JSON.stringify(data));
            }
            return;
        }
        this.pendingMessagesQueue.push(data);
    }

    closeSocket(options: { disableRetry?: boolean } = {}): void {
        if (options.disableRetry) {
            this.shouldRetry = false;
            this.hasAuthenticatedOnce = false;
            this.pendingMessagesQueue = [];
            if (this.visibilityListenerAttached) {
                document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
                this.visibilityListenerAttached = false;
                this.tabHiddenAt = null;
            }
            if (this.networkListenersAttached) {
                window.removeEventListener('online', this.onlineHandler);
                window.removeEventListener('offline', this.offlineHandler);
                this.networkListenersAttached = false;
                this.lastOfflineAt = null;
                this.lastOnlineAt = null;
            }
        }

        this.authenticatedSocket = false;
        this.connectTime = null;

        if (this.heartbeatSubscription) {
            this.heartbeatSubscription.unsubscribe();
            this.heartbeatSubscription = null;
        }
        if (this.socketSubscription) {
            this.socketSubscription.unsubscribe();
            this.socketSubscription = null;
        }
        if (this.connection$) {
            if (!this.connection$.closed) this.connection$.complete();
            this.connection$.unsubscribe();
            this.connection$ = null;
        }
    }

    private forceLogout(reason: ForceLogoutReason): void {
        this.shouldRetry = false;
        this.trackMixpanel(
            MIXPANEL_EVENTS.SOCKET_LOGOUT_FORCED,
            {
                socket_type: this.socketType,
                socket_url: Utils.sanitizeUrl(this.socketUrl),
                reason,
                internet_connected: navigator.onLine,
            },
            `forced_logout_${this.socketType}_${reason}`,
            true
        );
        this.closeSocket({ disableRetry: true });
        this.authProvider.logoutApi();
    }

    private getTimeMs(valueFromRemote: number | undefined, fallbackSeconds: number): number {
        const numericValue = Number(valueFromRemote);
        const seconds = Number.isFinite(numericValue) && numericValue > 0 ? numericValue : fallbackSeconds;
        return seconds * 1000;
    }

    private trackMixpanel(eventName: string, payload: any, dedupeKey: string, skipDedupe = false): void {
        if (!this.authProvider.isAuthenticated || !this.authProvider.accessTokenObj?.clientId) return;
        if (document.visibilityState === 'hidden') return;
        if (skipDedupe) {
            this.mixpanel.track(eventName, payload);
            return;
        }

        const now = Date.now();
        const dedupeState = this.mixpanelDedupeState.get(dedupeKey);
        if (dedupeState && (now - dedupeState.lastSentAt) < this.MIXPANEL_DEDUPE_WINDOW_MS) {
            dedupeState.suppressedCount += 1;
            return;
        }

        const eventPayload: any = { ...payload };
        if (dedupeState?.suppressedCount) {
            eventPayload.suppressed_count = dedupeState.suppressedCount;
        }

        this.mixpanel.track(eventName, eventPayload);
        this.mixpanelDedupeState.set(dedupeKey, {
            lastSentAt: now,
            suppressedCount: 0,
        });
    }
}
